/*
  X(Twitter) Ads helper for promotion web funnel.
  - Browser Pixel: window.twq 가 정의된 경우에만 실행 (App.tsx 에서 real 환경 한정 로드)
  - CAPI (Conversion API): BUILD_TARGET === "real" + identifier 존재 시
    백엔드 /ad/x-capi-web/event/send 로 전달. 백엔드가 SHA-256 hashing + OAuth1 서명 후
    X Ads API `/measurement/conversions/<pixel_id>` 로 포워딩.
  - Browser Pixel 과 CAPI 는 동일 `conversion_id` 로 dedup.
*/

import { v4 as uuidv4 } from "uuid";
import { BUILD_TARGET } from "config";
import { axiosInstance } from "../api";
import { getAdChannel } from "routes/adFunnel";

const X_PIXEL_ID = "ogz8m";
const TWCLID_COOKIE = "_twclid";
const CAPI_ENDPOINT = "/ad/x-capi-web/event/send";

type XEventKey = "PageView" | "Lead" | "AddPaymentInfo" | "Purchase";

const EVENT_ID_MAP: Record<XEventKey, string> = {
  PageView: "rbujx",
  Lead: "rbujz",
  AddPaymentInfo: "rbuk7",
  Purchase: "rbuk8",
};

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(name + "="));
  if (!match) return undefined;
  try {
    const raw = decodeURIComponent(match.split("=")[1]);
    if (!raw || raw === "null" || raw === "undefined") return undefined;
    return raw;
  } catch {
    return undefined;
  }
}

function setCookie(name: string, value: string, days = 90) {
  if (typeof document === "undefined") return;
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
}

function getQueryParam(param: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const v = new URL(window.location.href).searchParams.get(param);
  return v ?? undefined;
}

/**
 * URL 의 `twclid` (X 클릭 ID) 를 쿠키에 보존.
 * X 는 last-click attribution 이고 twclid 에 timestamp 가 embedded 되어 있지
 * 않으므로, URL 에 새 twclid 가 있으면 기존 쿠키를 덮어쓴다 (Meta fbc 패턴과 다름).
 */
export function ensureTwclidFromUrl(): string | undefined {
  const fromUrl = getQueryParam("twclid");
  if (fromUrl) {
    setCookie(TWCLID_COOKIE, fromUrl);
    return fromUrl;
  }
  return getCookie(TWCLID_COOKIE);
}

export function getTwclid(): string | undefined {
  return getCookie(TWCLID_COOKIE);
}

export interface XEventParams {
  value?: number | null;
  currency?: string | null;
  email?: string | null;
  conversionId?: string | null;
  status?: string | null;
}

export interface XCapiPayload {
  eventName: XEventKey;
  eventId: string; // tw-<pixel_id>-<event_id>
  pixelId: string; // ogz8m
  conversionId: string; // 브라우저 Pixel dedup 키
  conversionTime: number; // Unix seconds
  eventSourceUrl?: string;
  userData: {
    clientUserAgent?: string;
    em?: string; // plain email — 백엔드에서 SHA-256 hash
    twclid?: string;
  };
  customData?: {
    value?: number;
    currency?: string;
    status?: string;
  };
}

export function trackXAdsEvent(
  key: XEventKey,
  params: XEventParams = {}
): void {
  // X Ads 는 메타 퍼널 전용 — gads 퍼널(Google Ads)에서는 전송하지 않는다 (routes/adFunnel.ts)
  if (getAdChannel() === "google") return;
  try {
    ensureTwclidFromUrl();

    const conversionId = params.conversionId ?? uuidv4();
    const xEventId = `tw-${X_PIXEL_ID}-${EVENT_ID_MAP[key]}`;

    // 1) Browser Pixel
    if (typeof window !== "undefined") {
      const twq = (window as any).twq;
      if (twq) {
        const pixelPayload: Record<string, unknown> = {
          value: params.value ?? null,
          currency: params.currency ?? null,
          email_address: params.email ?? null,
          conversion_id: conversionId,
        };
        if (params.status !== undefined) {
          pixelPayload.status = params.status;
        }
        try {
          twq("event", xEventId, pixelPayload);
        } catch (_) {
          /* best-effort */
        }
      }
    }

    // 2) CAPI (server-side via our backend)
    if (BUILD_TARGET !== "real") return;

    const twclid = getTwclid();
    const email = params.email ?? undefined;
    // X CAPI 는 최소 1개 이상의 identifier (hashed_email / hashed_phone / twclid) 필수
    if (!email && !twclid) return;

    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : undefined;
    const eventSourceUrl =
      typeof window !== "undefined" ? window.location.href : undefined;

    const userData: XCapiPayload["userData"] = {
      ...(userAgent ? { clientUserAgent: userAgent } : {}),
      ...(email ? { em: email } : {}),
      ...(twclid ? { twclid } : {}),
    };

    const customData: XCapiPayload["customData"] = {};
    if (params.value !== null && params.value !== undefined) {
      customData.value = params.value;
    }
    if (params.currency) {
      customData.currency = params.currency;
    }
    if (params.status !== undefined && params.status !== null) {
      customData.status = params.status;
    }

    const payload: XCapiPayload = {
      eventName: key,
      eventId: xEventId,
      pixelId: X_PIXEL_ID,
      conversionId,
      conversionTime: Math.floor(Date.now() / 1000),
      eventSourceUrl,
      userData,
      ...(Object.keys(customData).length > 0 ? { customData } : {}),
    };

    // Fire-and-forget
    axiosInstance.post(CAPI_ENDPOINT, payload).catch(() => void 0);
  } catch (_) {
    // best-effort
  }
}
